import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import styles from './ui-chips.css';
import { Attribute } from '../../decorators/attribute.decorator';
import type { NullOr } from '../../global/utils.type';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIChips
 * @element ui-chips
 * @classdesc Represents a class for <code>ui-chips</code> element.
 * Chips are grouped together horizontally and cover the full width of container. Chips are either
 * side-scrollable or stacked on top of each other if they don't fit inside the container.
 * @property {boolean} [mobileScrollable] {@attr mobile-scrollable}
 * Makes chips side scrollable on mobile.
 * @slot {@type "ui-chip"}
 * @example
 * <ui-chips>
 *   <ui-chip label="Chip"></ui-chip>
 *   <ui-chip label="Chip"></ui-chip>
 * </ui-chips>
 */
@Component({ tag: 'ui-chips', styles })
export class UIChips extends UIElement {
    @Attribute(Boolean) mobileScrollable?: boolean;

    /**
     * Handle scroll behaviour
     * @param {Event} event
     * @private
     */
    private onScroll = (event: Event): void => {
        const target: NullOr<EventTarget> = event?.target;
        if (target instanceof HTMLElement) {
            const startReached = target.scrollLeft === 0;
            const endReached =
                Math.round(target.scrollLeft + target.offsetWidth) ===
                target.scrollWidth;
            let gradient = '--ui-chips-gradient-middle';
            if (startReached) {
                gradient = '--ui-chips-gradient-start';
            } else if (endReached) {
                gradient = '--ui-chips-gradient-end';
            }
            target.style.setProperty('--ui-chips-gradient', `var(${gradient})`);
        }
    };

    /**
     * @private
     */
    private subscribeEvents(): void {
        this.addEventListener('scroll', this.onScroll);
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        this.removeEventListener('scroll', this.onScroll);
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
    }
}
